import { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, User, Shield, Star, Eye, Zap, Users, Target, Building2, Crown, GitBranch } from 'lucide-react';
import type { Contact, ContactUseCaseScore, AccountDepartment } from '../../../types';
import {
  getHighlightFromPriority,
  getEffectivePriority,
  groupContactsByDepartment,
  HIGHLIGHT_COLORS,
  HIGHLIGHT_LABELS,
  BUYING_ROLE_LABELS,
  SENIORITY_LABELS,
  PRIORITY_LABELS,
  type ContactHighlight,
} from '../../../lib/orgChartUtils';

interface Props {
  contacts: Contact[];
  departments: AccountDepartment[];
  ucScoreMap: Map<string, ContactUseCaseScore>;
  onSelectContact: (contact: Contact) => void;
  selectedContactId?: string;
  onAssignManager: (contact: Contact) => void;
}

function BuyingRoleIcon({ role }: { role: string }) {
  switch (role) {
    case 'economic_buyer': return <Shield className="w-3 h-3 text-app-critical" />;
    case 'technical_buyer': return <Zap className="w-3 h-3 text-app-focus" />;
    case 'champion': return <Star className="w-3 h-3 text-app-warning" />;
    case 'influencer': return <Users className="w-3 h-3 text-app-warning" />;
    case 'validator': return <Eye className="w-3 h-3 text-app-focus" />;
    default: return null;
  }
}

function ContactRow({ contact, ucScore, isSelected, onSelect, onAssignManager }: {
  contact: Contact;
  ucScore?: ContactUseCaseScore;
  isSelected: boolean;
  onSelect: (c: Contact) => void;
  onAssignManager: (c: Contact) => void;
}) {
  const effectivePriority = ucScore ? getEffectivePriority(ucScore) : null;
  const highlight = effectivePriority
    ? getHighlightFromPriority(effectivePriority)
    : 'low';
  const colors = HIGHLIGHT_COLORS[highlight];
  const priorityLabel = effectivePriority
    ? PRIORITY_LABELS[effectivePriority] || ''
    : '';
  const isOverridden = ucScore?.priority_override != null;

  return (
    <div className="flex items-start gap-2 group">
      <button
        onClick={() => onSelect(contact)}
        data-contact-id={contact.id}
        className={`flex-1 text-left p-3 rounded-lg border-2 transition hover:shadow-none ${
          isSelected
            ? 'border-forest-500 bg-forest-50 ring-2 ring-forest-200'
            : `${colors.bg} ${colors.border}`
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${colors.bg}`}>
              <User className={`w-4 h-4 ${colors.text}`} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="text-sm font-semibold text-app-ink truncate">
                  {contact.first_name} {contact.last_name}
                </p>
                <BuyingRoleIcon role={contact.buying_committee_role} />
                {contact.is_inferred && (
                  <span className="text-[9px] font-medium px-1.5 py-0.5 bg-app-focus/10 text-app-focus rounded">Inferred</span>
                )}
              </div>
              <p className="text-xs text-app-muted truncate">{contact.role}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <div className="flex flex-col items-end gap-1">
              {priorityLabel && highlight !== 'low' && (
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${colors.bg} ${colors.text}`}>
                  {priorityLabel}
                  {isOverridden && <span className="ml-1 text-[8px] text-app-warning">(OVR)</span>}
                </span>
              )}
              {ucScore?.persona_match && (
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-forest-100 text-forest-700 truncate max-w-[120px]">
                  {ucScore.persona_match}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-1.5">
          {contact.seniority && (
            <span className="text-[10px] text-app-muted">
              {SENIORITY_LABELS[contact.seniority]?.split('(')[0]?.trim()}
            </span>
          )}
          {contact.buying_committee_role && (
            <span className="text-[10px] text-app-muted">
              {BUYING_ROLE_LABELS[contact.buying_committee_role]}
            </span>
          )}
          {ucScore && (
            <span className="text-[10px] text-app-muted ml-auto">
              {ucScore.use_case_relevance_score}% relevance
            </span>
          )}
        </div>
      </button>
      <button
        onClick={e => {
          e.stopPropagation();
          onAssignManager(contact);
        }}
        className="mt-2 p-1.5 text-app-line-strong hover:text-forest-600 hover:bg-forest-50 rounded-lg transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100"
        title="Assign manager"
      >
        <GitBranch className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function DepartmentSection({ deptName, contacts, department, ucScoreMap, onSelectContact, selectedContactId, onAssignManager, defaultExpanded }: {
  deptName: string;
  contacts: Contact[];
  department?: AccountDepartment;
  ucScoreMap: Map<string, ContactUseCaseScore>;
  onSelectContact: (contact: Contact) => void;
  selectedContactId?: string;
  onAssignManager: (contact: Contact) => void;
  defaultExpanded: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const sortedContacts = useMemo(() => {
    return [...contacts].sort((a, b) => {
      const scoreA = ucScoreMap.get(a.id);
      const scoreB = ucScoreMap.get(b.id);
      const prioA = scoreA ? getEffectivePriority(scoreA) : 'low_relevance';
      const prioB = scoreB ? getEffectivePriority(scoreB) : 'low_relevance';
      const orderMap: Record<string, number> = {
        target_persona: 0,
        decision_maker: 1,
        influencer: 2,
        validator: 3,
        low_relevance: 4,
      };
      const diff = (orderMap[prioA] ?? 4) - (orderMap[prioB] ?? 4);
      if (diff !== 0) return diff;
      return a.org_level - b.org_level;
    });
  }, [contacts, ucScoreMap]);

  const highPriorityCount = contacts.filter(c => {
    const s = ucScoreMap.get(c.id);
    if (!s) return false;
    const ep = getEffectivePriority(s);
    return ep === 'target_persona' || ep === 'decision_maker';
  }).length;

  const isRelevant = department?.is_relevant ?? false;
  const headDisplay = department?.head_name
    ? `${department.head_name}${department.head_title ? ` - ${department.head_title}` : ''}`
    : null;

  return (
    <div className={`rounded-lg border ${isRelevant ? 'border-forest-200 bg-forest-50/30' : 'border-app-line bg-app-surface'}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-app-canvas/50 transition-colors rounded-lg"
      >
        <div className="flex-shrink-0">
          {expanded
            ? <ChevronDown className="w-4 h-4 text-app-muted" />
            : <ChevronRight className="w-4 h-4 text-app-muted" />
          }
        </div>
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Building2 className={`w-4 h-4 flex-shrink-0 ${isRelevant ? 'text-forest-500' : 'text-app-muted'}`} />
          <span className={`text-sm font-semibold ${isRelevant ? 'text-forest-800' : 'text-app-ink'}`}>
            {deptName}
          </span>
          {isRelevant && (
            <Target className="w-3 h-3 text-forest-500 flex-shrink-0" />
          )}
          {headDisplay && (
            <span className="text-[10px] text-app-muted flex items-center gap-1 truncate">
              <Crown className="w-3 h-3 flex-shrink-0" />
              {headDisplay}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {highPriorityCount > 0 && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-app-critical/10 text-app-critical">
              {highPriorityCount} key
            </span>
          )}
          <span className="text-xs text-app-muted">{contacts.length}</span>
        </div>
      </button>
      {expanded && contacts.length > 0 && (
        <div className="px-4 pb-4 space-y-2">
          {department?.relevance_rationale && isRelevant && (
            <p className="text-[10px] text-forest-600 italic px-1 pb-1">{department.relevance_rationale}</p>
          )}
          {sortedContacts.map(contact => (
            <ContactRow
              key={contact.id}
              contact={contact}
              ucScore={ucScoreMap.get(contact.id)}
              isSelected={selectedContactId === contact.id}
              onSelect={onSelectContact}
              onAssignManager={onAssignManager}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function HighlightLegend() {
  return (
    <div className="flex flex-wrap items-center gap-2.5 ml-auto">
      {(Object.entries(HIGHLIGHT_COLORS) as [ContactHighlight, typeof HIGHLIGHT_COLORS[ContactHighlight]][]).map(([key, colors]) => (
        <div key={key} className="flex items-center gap-1">
          <div className={`w-2 h-2 rounded-full ${colors.dot}`} />
          <span className="text-[10px] text-app-muted">{HIGHLIGHT_LABELS[key]}</span>
        </div>
      ))}
    </div>
  );
}

export default function OrgChartDepartmentView({ contacts, departments, ucScoreMap, onSelectContact, selectedContactId, onAssignManager }: Props) {
  const grouped = useMemo(
    () => groupContactsByDepartment(contacts, departments),
    [contacts, departments]
  );

  const deptMap = useMemo(() => {
    const map = new Map<string, AccountDepartment>();
    for (const d of departments) {
      map.set(d.name, d);
    }
    return map;
  }, [departments]);

  const avgConfidence = contacts.length > 0
    ? Math.round(contacts.reduce((sum, c) => sum + c.org_placement_confidence, 0) / contacts.length)
    : 0;

  const targetCount = contacts.filter(c => {
    const s = ucScoreMap.get(c.id);
    if (!s) return false;
    const ep = getEffectivePriority(s);
    return ep === 'target_persona' || ep === 'decision_maker';
  }).length;

  const scoredContacts = contacts.filter(c => {
    const s = ucScoreMap.get(c.id);
    return s && s.persona_match_score > 0;
  });
  const avgPersonaMatch = scoredContacts.length > 0
    ? Math.round(scoredContacts.reduce((sum, c) => sum + (ucScoreMap.get(c.id)?.persona_match_score || 0), 0) / scoredContacts.length)
    : 0;

  if (contacts.length === 0) {
    return (
      <div className="text-center py-12">
        <Users className="w-10 h-10 text-app-line-strong mx-auto mb-3" />
        <p className="text-sm text-app-muted">No contacts to display.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-app-surface border border-app-line rounded-lg text-[11px] font-medium text-app-muted">
          Accuracy <span className="font-bold text-app-ink">{avgConfidence}%</span>
        </span>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-app-surface border border-app-line rounded-lg text-[11px] font-medium text-app-muted">
          Key Targets <span className="font-bold text-app-ink">{targetCount}</span>
        </span>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-app-surface border border-app-line rounded-lg text-[11px] font-medium text-app-muted">
          Persona Match <span className="font-bold text-app-ink">{avgPersonaMatch}%</span>
        </span>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-app-surface border border-app-line rounded-lg text-[11px] font-medium text-app-muted">
          Departments <span className="font-bold text-forest-700">{grouped.size}</span>
        </span>
        <HighlightLegend />
      </div>

      <div className="space-y-2">
        {Array.from(grouped.entries()).map(([deptName, deptContacts], idx) => (
          <DepartmentSection
            key={deptName}
            deptName={deptName}
            contacts={deptContacts}
            department={deptMap.get(deptName)}
            ucScoreMap={ucScoreMap}
            onSelectContact={onSelectContact}
            selectedContactId={selectedContactId}
            onAssignManager={onAssignManager}
            defaultExpanded={idx < 5 && (deptMap.get(deptName)?.is_relevant ?? deptContacts.length > 0)}
          />
        ))}
      </div>
    </div>
  );
}
